<template>
  <Box>
    <BoxHead
      icon="fas fa-file-alt"
      :link="link"
    >
      Reports

      <template
        v-if="followupLinkEnabled"
        #right
      >
        <span>View in</span>
        <router-link
          :to="{
            name: 'reports',
            query: { values_from: linkedEntityExternalId },
          }"
        >
          Impact Reports
        </router-link>

        <router-link
          :to="{
            name: 'fund-screener',
            query: { values_from: linkedEntityExternalId },
          }"
        >
          Fund Screener
        </router-link>

        <a
          :href="companyScreenerURL"
        >
          Company Screener
        </a>
      </template>
    </BoxHead>

    <LoaderWithText
      v-if="isFetching"
      size="3_5"
    />

    <div
      v-else
      class="reports-body"
    >
      <div
        v-for="(value, key) in results"
        :key="key"
        class="reports-result"
      >
        <Subtitle>
          {{ value.label }}
        </Subtitle>

        <Level>
          <LevelLeft>
            <Badge
              v-if="value.completed"
              status="success"
            >
              Completed
            </Badge>
            <Badge
              v-else
              status="danger"
            >
              Not completed
            </Badge>
          </LevelLeft>
          <LevelRight>
            <Button
              v-if="value.completed && value.url"
              size="3"
              icon-after="fas fa-external-link-alt"
              :href="value.url"
              target="_blank"
            >
              View results
            </Button>
          </LevelRight>
        </Level>
      </div>
    </div>
  </Box>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Badge, Box, BoxHead, Button, Level, LevelLeft, LevelRight, LoaderWithText, Subtitle } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';

const advisorStore = useAdvisorStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
});

const isFetching = ref(false);
const hasFetched = ref(false);
const results = ref({
  behavioral: {
    label: 'Behavioral Questionnaire',
    completed: false,
    date: null,
    url: '',
  },
  risk: {
    label: 'Risk Questionnaire',
    completed: false,
    date: null,
    url: '',
  },
  values: {
    label: 'Values Picker',
    completed: false,
    date: null,
    url: '',
  },
});
const linkedEntityExternalId = ref('');

const fetchData = async () => {
  isFetching.value = true;

  try {
    const response = await fetch(`/api/v1/entities/${props.entityExternalId}/get_linked_prospect_client_portfolio/`, {
      method: 'GET',
      headers: advisorStore.headers,
    });


    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    const data = await response.json();

    if (!data.external_id) {
      throw new Error(`No external id found.`);
    }

    linkedEntityExternalId.value = data.external_id;
    const baseUrl = `${window.location.protocol}//${window.location.host}`;

    // Behavioral Questionnaire: has_questionnaire_response
    // Risk Questionnaire: risk_questionnaire_score
    // Values Picker: has_wizard_screen_sets
    results.value = {
      ...results.value,
      behavioral: {
        ...results.value.behavioral,
        completed: data.has_questionnaire_response,
        date: data.questionnaire_date,
        url: `${baseUrl}/questionnaire/review-results/${linkedEntityExternalId.value}/`,
      },
      risk: {
        ...results.value.risk,
        completed: data.risk_questionnaire_score != null,
        date: data.risk_questionnaire_date,
        url: `${baseUrl}/ui/advisor/risk/results/${linkedEntityExternalId.value}/`,
      },
      values: {
        ...results.value.values,
        completed: data.has_wizard_screen_sets,
        date: data.wizard_screen_sets_date,
        url: `${baseUrl}/ui/advisor/wizard/results?prospect=${linkedEntityExternalId.value}`,
      },
    };
  } catch (error) {
    console.error(`Error fetching`, error);
  } finally {
    isFetching.value = false;
    hasFetched.value = true;
  }
};

const link = computed(() => {
  return {
    to: {
      name: 'entity-reports',
    },
    label: 'Setup Reports',
  };
});

const companyScreenerURL = computed(() => {
  if (!linkedEntityExternalId.value) {
    return;
  }

  const url = new URL('/advisordashboard/portfolio-builder/', window.location.origin);
  url.searchParams.set('values_from', linkedEntityExternalId.value);
  return url.toString();
});

const followupLinkEnabled = computed(() => {
  if (!linkedEntityExternalId.value) {
    return false;
  }

  return results.value.behavioral.completed || results.value.values.completed;
});

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.reports-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20em, 1fr));
  gap: 1em;
}

.reports-result {
  background-color: #F9FAFB;
  padding: 0.75em 1em;
  border-radius: 0.75em;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}
</style>
